import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useGlobalConnect, CountryStats } from "@/hooks/useGlobalConnect";
import { useCountryLiveStats, SceneType } from "@/hooks/useScenes";
import { useGeoData } from "@/hooks/useGeoData";
import GlobalConnectCountryCard from "./GlobalConnectCountryCard";
import SceneSelectorSheet from "./SceneSelectorSheet";

interface Country {
  code: string;
  name: string;
  flag: string;
  is_active: boolean;
}

interface CountryWithStats extends Country {
  stats?: CountryStats;
  timezone_offset?: string;
}

// Comprehensive continent groupings for ALL countries
const continentGroups: Record<string, string[]> = {
  Europe: [
    "AL", "AD", "AT", "AX", "BY", "BE", "BA", "BG", "HR", "CY", "CZ", "DK", 
    "EE", "FO", "FI", "FR", "DE", "GI", "GR", "GG", "HU", "IS", "IE", "IM", 
    "IT", "JE", "XK", "LV", "LI", "LT", "LU", "MT", "MD", "MC", "ME", "NL", 
    "MK", "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK", "SI", "ES", "SJ", 
    "SE", "CH", "UA", "GB", "VA"
  ],
  "North America": [
    "AI", "AG", "AW", "BS", "BB", "BZ", "BM", "BQ", "VG", "CA", "KY", "CR", 
    "CU", "CW", "DM", "DO", "SV", "GL", "GD", "GP", "GT", "HT", "HN", "JM", 
    "MQ", "MX", "MS", "NI", "PA", "PR", "BL", "KN", "LC", "MF", "PM", "VC", 
    "SX", "TT", "TC", "US", "VI"
  ],
  "South America": [
    "AR", "BO", "BR", "CL", "CO", "EC", "FK", "GF", "GY", "PY", "PE", "SR", 
    "UY", "VE"
  ],
  Asia: [
    "AF", "AM", "AZ", "BD", "BT", "BN", "KH", "CN", "GE", "HK", "IN", "ID", 
    "JP", "KZ", "KG", "LA", "MO", "MY", "MV", "MN", "MM", "NP", "KP", "PK", 
    "PH", "SG", "KR", "LK", "TW", "TJ", "TH", "TL", "TM", "UZ", "VN"
  ],
  "Middle East": [
    "BH", "EG", "IR", "IQ", "IL", "JO", "KW", "LB", "OM", "PS", "QA", "SA", 
    "SY", "TR", "AE", "YE"
  ],
  Africa: [
    "DZ", "AO", "BJ", "BW", "BF", "BI", "CV", "CM", "CF", "TD", "KM", "CG", 
    "CD", "CI", "DJ", "GQ", "ER", "SZ", "ET", "GA", "GM", "GH", "GN", "GW", 
    "KE", "LS", "LR", "LY", "MG", "MW", "ML", "MR", "MU", "YT", "MA", "MZ", 
    "NA", "NE", "NG", "RE", "RW", "ST", "SN", "SC", "SL", "SO", "ZA", "SS", 
    "SD", "TZ", "TG", "TN", "UG", "EH", "ZM", "ZW"
  ],
  Oceania: [
    "AS", "AU", "CK", "FJ", "PF", "GU", "KI", "MH", "FM", "NR", "NC", "NZ", 
    "NU", "NF", "MP", "PW", "PG", "PN", "WS", "SB", "TK", "TO", "TV", "VU", 
    "WF"
  ],
  Caribbean: [
    "AI", "AG", "AW", "BS", "BB", "BQ", "VG", "KY", "CU", "CW", "DM", "DO", 
    "GD", "GP", "HT", "JM", "MQ", "MS", "PR", "BL", "KN", "LC", "MF", "VC", 
    "SX", "TT", "TC", "VI"
  ],
  "Central Asia": [
    "KZ", "KG", "TJ", "TM", "UZ"
  ],
};

export const GlobalConnectCountryExplorer = () => {
  const navigate = useNavigate();
  const { getCountryStats } = useGlobalConnect();
  const { getCountries, getCountryFlag } = useGeoData();
  const [countries, setCountries] = useState<CountryWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContinent, setSelectedContinent] = useState<string | null>(null);
  
  // Scene selector state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [autoSelectScene, setAutoSelectScene] = useState(false);

  // Get live stats for all countries
  const countryCodes = useMemo(() => countries.map((c) => c.code), [countries]);
  const liveStats = useCountryLiveStats(countryCodes);

  useEffect(() => {
    // Guard: Ensure functions are available before calling
    if (!getCountries || !getCountryFlag || !getCountryStats) {
      console.warn("⚠️ GeoData functions not ready yet");
      return;
    }

    let cancelled = false;
    
    const fetchCountries = async () => {
      try {
        setLoading(true);
        console.log("🔄 Fetching countries from useGeoData...", searchQuery ? `(search: "${searchQuery}")` : "");
        
        // Use useGeoData hook to get countries (with search if provided)
        const geoCountries = await getCountries(searchQuery || undefined);
        
        if (cancelled) return;
        
        console.log(`✅ Fetched ${geoCountries.length} countries`);

        // Transform GeoCountry to Country format
        const transformedCountries: Country[] = geoCountries.map((geoCountry) => ({
          code: geoCountry.iso2,
          name: geoCountry.name,
          flag: getCountryFlag(geoCountry.iso2),
          is_active: true, // All countries from geo data are active
        }));

        // Fetch stats for featured countries (with error handling)
        const featuredCodes = ["JP", "BR", "FR", "AU", "MX", "DE"];
        const countriesWithStats = await Promise.all(
          transformedCountries.map(async (country) => {
            if (featuredCodes.includes(country.code)) {
              try {
                const stats = await getCountryStats(country.code);
                return { ...country, stats };
              } catch (err) {
                console.warn(`Failed to fetch stats for ${country.code}:`, err);
                return country;
              }
            }
            return country;
          })
        );

        if (cancelled) return;

        console.log(`✅ Set ${countriesWithStats.length} countries with stats`);
        setCountries(countriesWithStats);
      } catch (error) {
        if (cancelled) return;
        console.error("❌ Error fetching countries:", error);
        // Set empty array on error to prevent crash
        setCountries([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCountries();
    
    return () => {
      cancelled = true;
    };
  }, [searchQuery, getCountries, getCountryFlag, getCountryStats]);

  const openSceneSheet = (country: Country, autoSelect: boolean) => {
    setSelectedCountry(country);
    setAutoSelectScene(autoSelect);
    setSheetOpen(true);
  };

  const handleSceneSelect = (cityId: string, cityName: string, scene: SceneType) => {
    if (!selectedCountry) return;
    navigate(`/global-connect/${selectedCountry.code}/scene/${cityId}/${scene}`);
  };

  // Filter countries by continent (search is handled by getCountries)
  const filteredCountries = useMemo(() => {
    if (selectedContinent) {
      return countries.filter((country) =>
        continentGroups[selectedContinent]?.includes(country.code)
      );
    }
    return countries;
  }, [countries, selectedContinent]);

  // Get featured countries (those with stats or with live users)
  const featuredCountries = useMemo(() => {
    return countries
      .filter((c) => c.stats || (liveStats[c.code]?.liveCount || 0) > 0)
      .sort((a, b) => (liveStats[b.code]?.liveCount || 0) - (liveStats[a.code]?.liveCount || 0))
      .slice(0, 6);
  }, [countries, liveStats]);

  // Group remaining countries by continent
  const groupedCountries: Record<string, CountryWithStats[]> = {};
  
  filteredCountries.forEach((country) => {
    let foundContinent = false;
    for (const [continent, codes] of Object.entries(continentGroups)) {
      if (codes.includes(country.code)) {
        if (!groupedCountries[continent]) {
          groupedCountries[continent] = [];
        }
        groupedCountries[continent].push(country);
        foundContinent = true;
        break;
      }
    }
    // Add to "Other" if not in any continent group
    if (!foundContinent) {
      if (!groupedCountries["Other"]) {
        groupedCountries["Other"] = [];
      }
      groupedCountries["Other"].push(country);
    }
  });

  // Define continent display order
  const continentOrder = [
    "Europe", "North America", "South America", "Asia", 
    "Middle East", "Africa", "Oceania", "Caribbean", "Central Asia", "Other"
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search countries..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Continent Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        <button
          onClick={() => setSelectedContinent(null)}
          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
            !selectedContinent
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          All
        </button>
        {continentOrder.filter(c => c !== "Other").map((continent) => (
          <button
            key={continent}
            onClick={() => setSelectedContinent(continent)}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
              selectedContinent === continent
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {continent}
          </button>
        ))}
      </div>

      {/* Featured Countries with Live Energy */}
      {!searchQuery && !selectedContinent && featuredCountries.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">
            🔥 Live Right Now
          </h2>
          <div className="space-y-3">
            {featuredCountries.map((country) => (
              <GlobalConnectCountryCard
                key={country.code}
                country={country}
                stats={liveStats[country.code]}
                onSelect={() => openSceneSheet(country, false)}
                onDropIn={() => openSceneSheet(country, true)}
                featured
              />
            ))}
          </div>
        </section>
      )}

      {/* Grouped Countries - Ordered by continent */}
      {continentOrder
        .filter((continent) => groupedCountries[continent]?.length > 0)
        .map((continent) => (
          <section key={continent}>
            <h2 className="text-sm font-semibold text-muted-foreground mb-3">
              {continent === "Other" ? "🌍 Other Regions" : continent}
            </h2>
            <div className="space-y-2">
              {groupedCountries[continent].map((country) => (
                <button
                  key={country.code}
                  onClick={() => openSceneSheet(country, false)}
                  className="w-full p-3 bg-card rounded-lg border border-border hover:border-primary/50 transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <div className="text-left">
                      <p className="font-medium">{country.name}</p>
                      {liveStats[country.code]?.liveCount > 0 && (
                        <div className="flex items-center gap-2 text-xs text-green-600">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                          </span>
                          <Users className="w-3 h-3" />
                          <span>{liveStats[country.code].liveCount} live</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </button>
              ))}
            </div>
          </section>
        ))}

      {filteredCountries.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No countries found</p>
        </div>
      )}

      {/* Scene Selector Sheet */}
      <SceneSelectorSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        country={selectedCountry}
        onSelectScene={handleSceneSelect}
        autoSelect={autoSelectScene}
      />
    </div>
  );
};
